import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { useEffect, useCallback } from 'react';

interface AnimatedOdometerProps {
  value: number | string;
  className?: string;
  duration?: number;
}

export default function AnimatedOdometer({
  value,
  className = '',
  duration = 1.5
}: AnimatedOdometerProps) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, useCallback((val: number) => Math.round(val), []));

  // Handle string values (like ">80%") - no animation needed
  const isStringValue = typeof value === 'string';
  const numericValue = typeof value === 'number' ? value : 0;

  useEffect(() => {
    if (isStringValue) return; // Skip animation for string values

    const animation = animate(count, numericValue, {
      duration,
      ease: [0.22, 1, 0.36, 1],
    });

    return animation.stop;
  }, [numericValue, count, duration, isStringValue]);

  return (
    <motion.span
      className={className}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {isStringValue ? (
        <motion.span>{value}</motion.span>
      ) : (
        <>
          <motion.span>{rounded}</motion.span>%
        </>
      )}
    </motion.span>
  );
}